import {
  BMButton,
  BMCalendar,
  BMCard,
  BMInputField,
  BMPrimitives,
  BMText,
  Locale,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCrossClose from "@traveloka/icon-kit-web/react/IcSystemCrossClose";
import { format, parse } from "date-fns";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Pressable, StyleSheet } from "react-native";
import { FullScreenDialog } from "../../../components";
import BMView from "../../../components/View/BMView";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import StickyContainer from "../Sticky";
import { HotelSearchFormValues } from "./types";

type Props = {
  isVisible: boolean;
  locale: string;
  closeDialog: () => void;
};

const dateFormat = "dd MMMM yyyy";

export default function CalendarPicker(props: Props) {
  const { isVisible, locale, closeDialog } = props;
  const bmTheme = useBMTheme();
  const form = useFormContext<HotelSearchFormValues>();
  const [checkInDate, setCheckInDate] = useState<Date>(
    parse(form.getValues("checkInDate"), dateFormat, new Date())
  );
  const [checkOutDate, setCheckOutDate] = useState<Date>(
    parse(form.getValues("checkOutDate"), dateFormat, new Date())
  );

  const handleDateChange = (dateRange: [Date, Date | undefined]) => {
    setCheckInDate(dateRange[0]);
    if (dateRange[1]) {
      setCheckOutDate(dateRange[1]);
    }
  };

  const handleSaveCalendar = () => {
    form.setValue("checkInDate", format(checkInDate, dateFormat));
    form.setValue("checkOutDate", format(checkOutDate, dateFormat));
    closeDialog();
  };

  const { CorporateHotelSearchForm } = useMobileContentResource();

  return (
    <FullScreenDialog
      style={{
        backgroundColor: bmTheme.BMComponents.BMStyleSurfaceBgcolor,
      }}
      visible={isVisible}
    >
      <StickyContainer
        headerContent={
          <BMView
            style={[
              {
                backgroundColor: bmTheme.BMComponents.BMStyleSurfaceBgcolorHero,
              },
              styles.container,
            ]}
          >
            <BMView align="end">
              <Pressable onPress={closeDialog}>
                <IcSystemCrossClose
                  width={24}
                  height={24}
                  color={bmTheme.BMComponents.BMStyleSurfaceBgcolor}
                />
              </Pressable>
            </BMView>
            <BMView style={styles.title}>
              <BMText variant="titleSmall" ink="BMStyleTextInkPrimaryInvert">
                {CorporateHotelSearchForm.calendarPickerTitle}
              </BMText>
            </BMView>
          </BMView>
        }
        footerContent={
          <BMView style={styles.footer}>
            <BMCard layout="SPACIOUS" elevation="FLOAT">
              <BMView row justify="between" spacing="sm">
                <BMView style={styles.footerButton}>
                  <BMButton
                    label={CorporateHotelSearchForm.cancel}
                    size="LARGE"
                    role="SECONDARY"
                    onPress={closeDialog}
                    stretchable
                  />
                </BMView>
                <BMView style={styles.footerButton}>
                  <BMButton
                    label={CorporateHotelSearchForm.confirm}
                    size="LARGE"
                    role="PRIMARY"
                    onPress={handleSaveCalendar}
                    stretchable
                  />
                </BMView>
              </BMView>
            </BMCard>
          </BMView>
        }
      >
        <BMView>
          <BMView
            row
            justify="between"
            spacing="md"
            style={[
              {
                borderBottomColor:
                  bmTheme.BMComponents.BMStyleLineBordercolorSubtle,
              },
              styles.inputs,
            ]}
          >
            <BMView style={styles.inputField}>
              <BMInputField
                label={CorporateHotelSearchForm.checkInLabel}
                value={format(checkInDate, dateFormat)}
              />
            </BMView>
            <BMView style={styles.inputField}>
              <BMInputField
                label={CorporateHotelSearchForm.checkOutLabel}
                value={format(checkOutDate, dateFormat)}
              />
            </BMView>
          </BMView>
          <BMView style={styles.calendar}>
            <BMCalendar
              variant="horizontal"
              numberOfRenderedMonth={1}
              autoScrollToSelectedDate={false}
              date={[checkInDate, checkOutDate]}
              locale={locale as Locale}
              hideHolidayBadges={false}
              onChange={handleDateChange}
            />
          </BMView>
        </BMView>
      </StickyContainer>
    </FullScreenDialog>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: BMPrimitives.BMSize.md,
  },
  title: {
    marginTop: BMPrimitives.BMSize.xl,
  },
  inputs: {
    paddingVertical: BMPrimitives.BMSize.md,
    paddingHorizontal: BMPrimitives.BMSize.lg,
    borderBottomWidth: BMPrimitives.BMStroke.sm,
  },
  calendar: {
    marginTop: BMPrimitives.BMSize.sm,
  },
  footer: {
    position: "absolute",
    width: "100%",
    bottom: 0,
  },
  footerButton: {
    width: "50%",
  },
  inputField: {
    width: "45%",
  },
});
